import { useState } from "react";

import { ApiError, scopedPath } from "@/lib/api";

/**
 * Downloads a PDF.
 *
 * Rendering runs in a separate container and takes a few seconds, so the button
 * says so rather than appearing to do nothing. A renderer that is down produces
 * a readable message, not a broken download.
 *
 * **Paths are written unscoped and expanded here, like every other call.** This
 * component calls `fetch` directly because it wants a blob rather than JSON,
 * and when the reports router moved under `/api/vessels/{id}` it was the one
 * caller the sweep missed — every export went on posting to a path that no
 * longer existed. The failure was not even a 404: an unmatched `/api/` POST
 * falls through to the SPA's static mount, which serves GET and answers 405,
 * so the browser reported "Method Not Allowed" for a method that was correct.
 */
export default function ReportButton({
  path,
  label,
  className = "btn btn-quiet w-full text-sm",
}: {
  path: string;
  label: string;
  className?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function download() {
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(scopedPath(path), {
        method: "POST",
        credentials: "same-origin",
      });
      if (!response.ok) {
        let detail = "Could not build that report.";
        try {
          detail = (await response.json()).detail ?? detail;
        } catch {
          /* non-JSON error body */
        }
        throw new Error(detail);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download =
        response.headers.get("Content-Disposition")?.match(/filename="(.+)"/)?.[1] ??
        "pool-report.pdf";
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      // `scopedPath` throws an ApiError when no vessel is selected, which is a
      // real message ("Pick one first") rather than a rendering failure.
      setError(
        err instanceof ApiError || err instanceof Error
          ? err.message
          : "Could not build that report.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <button type="button" onClick={download} disabled={busy} className={className}>
        {busy ? "Building PDF…" : label}
      </button>
      {error && (
        <p role="alert" className="mt-2 text-xs text-(--color-status-high)">
          {error}
        </p>
      )}
    </div>
  );
}
